// TelcoTrack - Mini Leaflet preview of excavation route in construction diary modal.

// Uses work-summary route polyline — no per-segment object API calls (rate-limit safe).



import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import * as L from 'leaflet';

import {

  computeRoutePreviewDimensions,

  type RoutePoint,

} from '../../../../core/utils/diary-work-summary-display.util';

import { environment } from '../../../../../environments/environment';
import {
  MAP_ROUTE_DIARY_PREVIEW_COLOR,
  MAP_ROUTE_DIARY_PREVIEW_OPACITY,
  MAP_ROUTE_DIARY_PREVIEW_WEIGHT,
} from '@core/shared-bridge';



interface DiaryRouteMapOptions {

  interactive: boolean;

  zoomControl: boolean;

  fitPadding: [number, number];

  maxZoom?: number;

}



@Component({

  selector: 'app-diary-work-route-preview',

  templateUrl: './diary-work-route-preview.component.html',

  styleUrls: ['./diary-work-route-preview.component.scss'],

  changeDetection: ChangeDetectionStrategy.OnPush,

})

export class DiaryWorkRoutePreviewComponent implements OnChanges, AfterViewInit, OnDestroy {

  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLElement>;

  @ViewChild('overlayRoot', { static: false }) overlayRoot?: ElementRef<HTMLElement>;

  @ViewChild('modalMapContainer', { static: false }) modalMapContainer?: ElementRef<HTMLElement>;

  /** Celá trasa skupiny (polyline vertices). */

  @Input() routePoints: RoutePoint[] = [];



  /** @deprecated Alias pro zpětnou kompatibilitu šablon. */

  @Input()

  set fallbackPoints(value: RoutePoint[]) {

    this.routePoints = value ?? [];

  }



  @HostBinding('style.width.px') hostWidth = 200;

  @HostBinding('style.height.px') hostHeight = 140;



  hasGeometry = false;

  expanded = false;



  private map: L.Map | null = null;

  private layerGroup: L.LayerGroup | null = null;

  private modalMap: L.Map | null = null;

  private modalLayerGroup: L.LayerGroup | null = null;

  private viewReady = false;

  private resizeObserver: ResizeObserver | null = null;

  private modalResizeObserver: ResizeObserver | null = null;

  private lastPointsKey = '';
  private overlayAnchor: HTMLElement | null = null;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly renderer: Renderer2,
  ) {}



  ngOnChanges(changes: SimpleChanges): void {

    if (changes['routePoints'] || changes['fallbackPoints']) {

      this.renderIfReady();

    }

  }



  ngAfterViewInit(): void {

    this.viewReady = true;

    this.initPreviewMap();

    this.renderIfReady();

  }



  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.modalResizeObserver?.disconnect();
    this.modalResizeObserver = null;
    this.detachOverlayFromBody();
    this.destroyModalMap();
    this.destroyPreviewMap();
  }

  openExpanded(): void {
    if (!this.hasGeometry) return;
    this.expanded = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.attachOverlayToBody();
      this.initModalMap();
      this.renderModalRoute(this.routePoints);
    }, 0);
  }

  closeExpanded(): void {
    this.expanded = false;
    this.detachOverlayFromBody();
    this.destroyModalMap();
    this.cdr.markForCheck();
  }

  private attachOverlayToBody(): void {
    const overlay = this.overlayRoot?.nativeElement;
    if (!overlay || overlay.parentElement === document.body) return;
    this.overlayAnchor = overlay.parentElement;
    this.renderer.appendChild(document.body, overlay);
  }

  private detachOverlayFromBody(): void {
    const overlay = this.overlayRoot?.nativeElement;
    if (!overlay || !this.overlayAnchor) return;
    this.renderer.appendChild(this.overlayAnchor, overlay);
    this.overlayAnchor = null;
  }



  private pointsKey(points: RoutePoint[]): string {

    return points.map((p) => `${p.lat},${p.lng}`).join('|');

  }



  private renderIfReady(): void {

    if (!this.viewReady || !this.map || !this.layerGroup) return;

    const key = this.pointsKey(this.routePoints);

    if (key === this.lastPointsKey) return;

    this.lastPointsKey = key;

    this.applyPreviewDimensions(this.routePoints);

    this.renderRoute(this.map, this.layerGroup, this.routePoints, {

      interactive: false,

      zoomControl: false,

      fitPadding: [12, 12],

      maxZoom: 17,

    });

    this.cdr.markForCheck();

  }



  private applyPreviewDimensions(points: RoutePoint[]): void {

    const { widthPx, heightPx } = computeRoutePreviewDimensions(points);

    this.hostWidth = widthPx;

    this.hostHeight = heightPx;

    const el = this.mapContainer?.nativeElement;

    if (el) {

      el.style.width = `${widthPx}px`;

      el.style.height = `${heightPx}px`;

    }

  }



  private initPreviewMap(): void {

    const el = this.mapContainer?.nativeElement;

    if (!el || this.map) return;

    this.map = this.createMap(el, {

      interactive: false,

      zoomControl: false,

      attributionControl: false,

    });

    this.addTileLayer(this.map, { withAttribution: false });
    this.layerGroup = L.layerGroup().addTo(this.map);
    this.observeMapResize(el, 'preview');

  }



  private initModalMap(): void {

    const el = this.modalMapContainer?.nativeElement;

    if (!el || this.modalMap) return;

    this.modalMap = this.createMap(el, {

      interactive: true,

      zoomControl: true,

      attributionControl: true,

    });

    this.addTileLayer(this.modalMap, { withAttribution: true });
    this.modalLayerGroup = L.layerGroup().addTo(this.modalMap);
    this.observeMapResize(el, 'modal');

  }



  private createMap(
    el: HTMLElement,
    options: Pick<DiaryRouteMapOptions, 'interactive' | 'zoomControl'> & { attributionControl: boolean },
  ): L.Map {

    return L.map(el, {

      zoomControl: options.zoomControl,

      attributionControl: options.attributionControl,

      scrollWheelZoom: options.interactive,

      dragging: options.interactive,

      doubleClickZoom: options.interactive,

      boxZoom: options.interactive,

      keyboard: options.interactive,

      touchZoom: options.interactive,

    });

  }



  private addTileLayer(map: L.Map, options: { withAttribution: boolean }): void {
    L.tileLayer(environment.maps.tileUrl, {
      maxZoom: 19,
      ...(options.withAttribution ? { attribution: environment.maps.attribution } : {}),
    }).addTo(map);
  }

  private observeMapResize(el: HTMLElement, target: 'preview' | 'modal'): void {
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const map = target === 'preview' ? this.map : this.modalMap;
      map?.invalidateSize({ animate: false });
    });
    observer.observe(el);
    if (target === 'preview') {
      this.resizeObserver?.disconnect();
      this.resizeObserver = observer;
    } else {
      this.modalResizeObserver?.disconnect();
      this.modalResizeObserver = observer;
    }
  }



  private destroyPreviewMap(): void {

    if (this.map) {

      this.map.remove();

      this.map = null;

      this.layerGroup = null;

    }

  }



  private destroyModalMap(): void {

    if (this.modalMap) {

      this.modalMap.remove();

      this.modalMap = null;

      this.modalLayerGroup = null;

    }

  }



  private renderModalRoute(points: RoutePoint[]): void {

    if (!this.modalMap || !this.modalLayerGroup) return;

    this.renderRoute(this.modalMap, this.modalLayerGroup, points, {

      interactive: true,

      zoomControl: true,

      fitPadding: [32, 32],

      maxZoom: 18,

    });

  }



  private renderRoute(

    map: L.Map,

    layerGroup: L.LayerGroup,

    points: RoutePoint[],

    options: DiaryRouteMapOptions,

  ): void {

    layerGroup.clearLayers();



    const bounds = L.latLngBounds([]);

    const drew = this.drawRoutePolyline(layerGroup, points, bounds);



    this.hasGeometry = drew;

    if (drew && bounds.isValid()) {

      map.fitBounds(bounds, {

        padding: options.fitPadding,

        maxZoom: options.maxZoom ?? 17,

      });

    }



    setTimeout(() => map.invalidateSize({ animate: false }), 0);

  }



  private drawRoutePolyline(layerGroup: L.LayerGroup, points: RoutePoint[], bounds: L.LatLngBounds): boolean {

    if (!points.length) return false;



    if (points.length === 1) {

      const p = L.latLng(points[0].lat, points[0].lng);

      L.circleMarker(p, {

        radius: 5,

        color: MAP_ROUTE_DIARY_PREVIEW_COLOR,

        fillColor: MAP_ROUTE_DIARY_PREVIEW_COLOR,

        fillOpacity: MAP_ROUTE_DIARY_PREVIEW_OPACITY,

        weight: 2,

      }).addTo(layerGroup);

      bounds.extend(p);

      return true;

    }



    const latlngs = points.map((p) => L.latLng(p.lat, p.lng));

    L.polyline(latlngs, {

      color: MAP_ROUTE_DIARY_PREVIEW_COLOR,

      weight: MAP_ROUTE_DIARY_PREVIEW_WEIGHT,

      opacity: MAP_ROUTE_DIARY_PREVIEW_OPACITY,

    }).addTo(layerGroup);

    for (const ll of latlngs) bounds.extend(ll);

    return true;

  }

}


